import { getWorstState } from './panelAggregator';

/**
 * Níveis de agrupamento dos cartões do painel.
 *
 * Regional ainda não está aqui, mas o impedimento acabou: desde que
 * `/StatusRede/listagem` passou a devolver `regionalId` e `regional`, cada Ativo
 * carrega o vínculo. Falta só decidir como exibir as instalações sem Regional,
 * que vêm com os dois campos nulos.
 */
export const groupingLevels = {
  COMPANY: 'company',
  INSTALLATION: 'installation',
  ASSET: 'asset',
};

export const DEFAULT_GROUPING = groupingLevels.INSTALLATION;

export function isValidGrouping(value) {
  return Object.keys(groupingLevels)
    .some(name => groupingLevels[name] === value);
}

function compareNames(left, right) {
  return String(left || '').localeCompare(String(right || ''), 'pt-BR', {
    numeric: true,
    sensitivity: 'base',
  });
}

/**
 * Como cada nível identifica, rotula e ordena um cartão.
 *
 * `sortKeys` é comparado item a item, então a ordem final é sempre estável e
 * previsível — requisito da memória espacial de quem observa o painel.
 */
const levelDefinitions = {
  [groupingLevels.COMPANY]: asset => ({
    key: `company::${asset.company}`,
    title: asset.company,
    subtitle: null,
    sortKeys: [asset.company],
  }),
  [groupingLevels.INSTALLATION]: asset => ({
    key: asset.installationId,
    title: asset.installation,
    subtitle: asset.company,
    sortKeys: [asset.company, asset.installation],
  }),
  [groupingLevels.ASSET]: asset => ({
    key: asset.key,
    title: asset.name,
    subtitle: `${asset.company} · ${asset.installation}`,
    sortKeys: [asset.company, asset.installation, asset.name],
  }),
};

/**
 * Contexto exibido antes do nome do Ativo na faixa.
 *
 * Agrupando por empresa, um cartão reúne ativos de instalações diferentes, e
 * nomes como "TR1" ou "03T1" se repetem entre elas. Sem a instalação não há como
 * saber de qual site é cada faixa.
 *
 * Vem separado do nome, e não concatenado, para que a instalação possa receber
 * menos peso visual: quem lê o cartão procura o ativo, e a instalação só desfaz
 * a ambiguidade.
 *
 * Nos outros níveis a instalação já está no cabeçalho do cartão, então repeti-la
 * em cada faixa seria ruído.
 */
function buildAssetContext(asset, grouping) {
  return grouping === groupingLevels.COMPANY ? asset.installation : null;
}

function compareBySortKeys(left, right) {
  const size = Math.max(left.sortKeys.length, right.sortKeys.length);

  for (let index = 0; index < size; index += 1) {
    const result = compareNames(left.sortKeys[index], right.sortKeys[index]);
    if (result !== 0) {
      return result;
    }
  }

  return 0;
}

/**
 * Agrupa os Ativos nos cartões exibidos.
 *
 * A faixa de um cartão é sempre um Ativo, em qualquer nível. O que muda
 * é quantos Ativos cabem no mesmo cartão: no nível Ativo, exatamente um.
 *
 * @param {Object[]} assets Lista plana de `buildAssets`.
 * @param {String} [grouping] Um valor de `groupingLevels`.
 * @returns {Object[]} Cartões ordenados, cada um com seus ativos.
 */
export function groupAssets(assets, grouping = DEFAULT_GROUPING) {
  const describe = levelDefinitions[grouping] || levelDefinitions[DEFAULT_GROUPING];
  const groups = {};
  const order = [];

  (assets || []).forEach((asset) => {
    const description = describe(asset);

    if (!groups[description.key]) {
      groups[description.key] = { ...description, assets: [] };
      order.push(description.key);
    }

    groups[description.key].assets.push(asset);
  });

  return order
    .map((key) => {
      const group = groups[key];
      const ordered = group.assets
        .slice()
        .sort((a, b) => compareNames(a.installation, b.installation)
          || compareNames(a.name, b.name))
        .map(asset => ({ ...asset, context: buildAssetContext(asset, grouping) }));

      return {
        ...group,
        assets: ordered,
        state: getWorstState(ordered.map(asset => asset.state)),
      };
    })
    .sort(compareBySortKeys);
}
